/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListMembersRequest,
  ListMembersSecurity,
} from "../models/operations/listmembers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMembersQuery,
  MembersQueryData,
  prefetchMembers,
  queryKeyMembers,
} from "./members.core.js";
export {
  buildMembersQuery,
  type MembersQueryData,
  prefetchMembers,
  queryKeyMembers,
};

export type MembersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMembers access
 *
 * @remarks
 * List all team members with their role assignments.
 */
export function useMembers(
  request?: ListMembersRequest | undefined,
  security?: ListMembersSecurity | undefined,
  options?: QueryHookOptions<MembersQueryData, MembersQueryError>,
): UseQueryResult<MembersQueryData, MembersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildMembersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMembers access
 *
 * @remarks
 * List all team members with their role assignments.
 */
export function useMembersSuspense(
  request?: ListMembersRequest | undefined,
  security?: ListMembersSecurity | undefined,
  options?: SuspenseQueryHookOptions<MembersQueryData, MembersQueryError>,
): UseSuspenseQueryResult<MembersQueryData, MembersQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMembersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMembersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: MembersQueryData,
): MembersQueryData | undefined {
  const key = queryKeyMembers(...queryKeyBase);

  return client.setQueryData<MembersQueryData>(key, data);
}

export function invalidateMembers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listMembers", ...queryKeyBase],
  });
}

export function invalidateAllMembers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listMembers"],
  });
}
